import React, { useEffect, useState, useRef } from 'react';

/**
 * LiveSeatMap
 * Fetches public seating map (party or conference) and renders a compact visualization.
 * Props:
 *  - apiBase (string) base API url (API_BASE_URL)
 *  - eventId (number|null)
 *  - seatingMode ('party'|'conference') optional override from eventConfig
 *  - highlightTable (number|string|null) table number to highlight
 *  - highlightSeats (array<number>|null) seats to highlight (within a table for party, global order for conference)
 */
export default function LiveSeatMap({ apiBase, eventId = null, seatingMode: modeProp, highlightTable, highlightSeats, showOnlyHighlightedInitial = true, seatMapVisibility: seatMapVisibilityProp }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showAll, setShowAll] = useState(!showOnlyHighlightedInitial || !highlightTable);

  // If highlightTable becomes available later (after initial mount / remount) ensure we collapse view
  useEffect(() => {
    if (showOnlyHighlightedInitial && highlightTable && showAll) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[LiveSeatMap] Auto-collapsing to single table', highlightTable);
      }
      setShowAll(false);
    }
  }, [highlightTable, showAll, showOnlyHighlightedInitial]);

  // Determine effective seat map visibility (prefer API response when available)
  const effectiveSeatMapVisibility = data?.seatMapVisibility || seatMapVisibilityProp;
  // Force showAll if visibility = 'all'
  useEffect(()=>{
    if (effectiveSeatMapVisibility === 'all' && !showAll) setShowAll(true);
  }, [effectiveSeatMapVisibility, showAll]);
  const liveRegionRef = useRef(null);
  const gridRef = useRef(null);

  // Unified seating mode usage only (assigned|open). Default to assigned.
  const seatingModeUnified = (() => {
    if (modeProp && ['assigned','open'].includes(modeProp)) return modeProp;
    if (data?.seatingModeUnified && ['assigned','open'].includes(data.seatingModeUnified)) return data.seatingModeUnified;
    return 'assigned';
  })();

  // Internal rendering style selection (was party/conference). Open => conference style.
  const renderMode = seatingModeUnified === 'open' ? 'conference' : 'party';

  // Track ongoing / recent loads to prevent bursts
  const lastLoadRef = useRef(0);
  const inFlightRef = useRef(false);
  const etagRef = useRef(null);

  const load = async (signal, { force=false } = {}) => {
    const now = Date.now();
    if (!force) {
      // Throttle: skip if a load finished < 1200ms ago or one is in-flight
      if (inFlightRef.current || (now - lastLoadRef.current) < 1200) return;
    }
    try {
      inFlightRef.current = true;
      setLoading(true); setError(null);
  // Narrow to single table when highlightTable provided and not showing all yet (privacy & performance)
  const singleTableQuery = (highlightTable && !showAll) ? `?table=${encodeURIComponent(highlightTable)}` : '';
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[LiveSeatMap] load()', { highlightTable, showAll, singleTableQuery });
  }
  const pathCore = eventId ? `/events/${eventId}/public/seating-map` : `/public/seating-map`;
  const path = pathCore + singleTableQuery;
      const baseTrimmed = (apiBase || '').replace(/\/$/, '');
      // Candidate URLs: as-is, ensure /api prefix (if missing), and raw (in case apiBase already includes /api)
      const hasApi = /\/api$/.test(baseTrimmed);
      const candidates = [
        `${baseTrimmed}${path}`,
        !hasApi ? `${baseTrimmed}/api${path}` : null,
        hasApi ? `${baseTrimmed.replace(/\/api$/, '')}${path}` : null
      ].filter(Boolean);
      let lastErr = null;
      for (const url of candidates) {
        try {
          const headers = {};
          if (etagRef.current) headers['If-None-Match'] = etagRef.current;
          const res = await fetch(url, { signal, headers });
          if (res.status === 304) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.log('[LiveSeatMap] 304 Not Modified, skipping update');
            }
            lastErr = null; // treat as success
            break;
          }
          if (res.status === 404) { lastErr = new Error(`HTTP 404 (${url})`); continue; }
          if (!res.ok) { lastErr = new Error(`HTTP ${res.status} (${url})`); continue; }
          let json = await res.json();
          // Safety: if we requested single table but backend returned multiple, filter client-side
          if (highlightTable && !showAll && Array.isArray(json.tables) && json.tables.length > 1) {
            const ht = Number(highlightTable);
            const filtered = json.tables.filter(t => Number(t.tableNumber) === ht);
            if (filtered.length) {
              if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.log('[LiveSeatMap] Client-side filtered extra tables', { before: json.tables.length, after: filtered.length, ht });
              }
              json = { ...json, tables: filtered };
            }
          }
          if (json) {
            // Capture ETag if present
            const et = res.headers.get('ETag'); if (et) etagRef.current = et;
            setData(json);
            setLastUpdated(new Date());
          }
          lastErr = null;
          break;
        } catch (inner) {
          if (inner.name === 'AbortError') return; // abort stops whole load
          lastErr = inner;
        }
      }
      if (lastErr) throw lastErr;
    } catch (e) {
      if (e.name !== 'AbortError') setError(e.message || 'Failed to load map');
    } finally { setLoading(false); inFlightRef.current = false; lastLoadRef.current = Date.now(); }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    // In single-table privacy mode, if highlightTable not yet known, defer load to avoid empty map flash
    if (showOnlyHighlightedInitial && !highlightTable) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[LiveSeatMap] Deferring initial load until highlightTable available');
      }
    } else {
      load(ctrl.signal);
    }
    const interval = setInterval(()=>{
      // Skip periodic refresh if still waiting on highlight table in single-table mode
      if (showOnlyHighlightedInitial && !highlightTable) return;
      load(ctrl.signal);
    }, 30000); // refresh every 30s
    // SSE real-time updates
    let es;
    let reconnectAttempts = 0;
    const baseTrimmed = (apiBase || '').replace(/\/$/, '');
    const hasApi = /\/api$/.test(baseTrimmed);
    const candidateSse = [
      // If apiBase already ends with /api, first try with suffix path only once
      hasApi ? `${baseTrimmed}/public/seating-stream` : `${baseTrimmed}/api/public/seating-stream`,
      // Fallback to alternative form
      hasApi ? `${baseTrimmed.replace(/\/api$/, '')}/api/public/seating-stream` : `${baseTrimmed}/public/seating-stream`
    ].filter((v, i, arr) => arr.indexOf(v) === i); // de-dupe

    const openSse = () => {
      const url = candidateSse[reconnectAttempts % candidateSse.length];
      try {
        es = new EventSource(url);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[LiveSeatMap] Opening SSE', url);
        }
        let queued = false;
        es.addEventListener('seat:update', (ev) => {
          if (queued) return; // debounce bursts
            queued = true;
            setTimeout(()=>{ queued = false; }, 800);
            try {
              const info = JSON.parse(ev.data);
              if (info.version && (!data || info.version > (data.version || 0))) {
                load(ctrl.signal, { force:false });
              }
            } catch(_){}
        });
        es.onerror = () => {
          try { es.close(); } catch(_){}
          reconnectAttempts += 1;
          const delay = Math.min(5000, 500 * reconnectAttempts); // capped backoff
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.log('[LiveSeatMap] SSE error; scheduling reconnect in', delay, 'ms');
          }
          setTimeout(() => {
            // Force a refresh after some failed attempts
            if (reconnectAttempts % 3 === 0) load(ctrl.signal, { force:true });
            openSse();
          }, delay);
        };
      } catch (e) {
        reconnectAttempts += 1;
        const delay = Math.min(5000, 500 * reconnectAttempts);
        setTimeout(openSse, delay);
      }
    };
    openSse();
    return () => { try { es && es.close(); } catch(_){} ctrl.abort(); clearInterval(interval); };
  // Intentionally omit 'data' from deps to avoid re-creating SSE every data update.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase, eventId, highlightTable, showAll, showOnlyHighlightedInitial]);

  // If we previously deferred (or got empty due to missing table param) and now have a highlightTable, auto-load once.
  useEffect(() => {
    if (showOnlyHighlightedInitial && highlightTable && (!data || (Array.isArray(data.tables) && data.tables.length === 0)) && !loading) {
      const ctrl = new AbortController();
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[LiveSeatMap] Auto-loading after highlightTable became available');
      }
      load(ctrl.signal);
      return () => ctrl.abort();
    }
  }, [showOnlyHighlightedInitial, highlightTable, data, loading]);

  // Announce updates (must be declared before any conditional returns to satisfy hooks rule)
  const announceUpdate = () => {
    if (liveRegionRef.current) {
      liveRegionRef.current.textContent = `Seat map updated at ${new Date().toLocaleTimeString()}`;
    }
  };
  useEffect(() => { if (data) announceUpdate(); }, [data]);

  const legend = (
    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }} aria-hidden="true">
      {[
        ['available','#e9ecef','Available'],
        ['assigned','#ffc107','Assigned'],
        ['reserved','#fd7e14','Reserved'],
        ['occupied','#0d6efd','Checked In'],
        ['you','#198754','You']
      ].map(([key,color,label]) => (
        <div key={key} style={{ display:'flex', alignItems:'center', gap:4, fontSize:'0.55rem' }}>
          <span style={{ width:12, height:12, background:color, borderRadius:3, display:'inline-block' }} /> {label}
        </div>
      ))}
      {lastUpdated && <span style={{ fontSize:'0.5rem', opacity:0.7 }}>Updated {lastUpdated.toLocaleTimeString()}</span>}
    </div>
  );

  if (error) {
    return <div style={{ fontSize:'0.6rem', color:'#dc3545' }}>Seat map error: {error}</div>;
  }
  if (loading && !data) {
    return <div style={{ fontSize:'0.65rem' }}>Loading seat map...</div>;
  }

  if (!data) return null;

  const filteredTables = (() => {
    if (!data) return [];
    if (showAll || !highlightTable) return data.tables;
    return data.tables.filter(t => Number(t.tableNumber) === Number(highlightTable));
  })();

  // Basic keyboard navigation within seats (arrow keys)
  const handleKeyDown = (e) => {
    const current = document.activeElement;
    if (!current || !current.dataset || !current.dataset.seatIndex) return;
    const seats = Array.from(gridRef.current.querySelectorAll('[data-seat-index]'));
    const idx = seats.indexOf(current);
    if (idx === -1) return;
    let nextIdx = idx;
    const cols = 10; // heuristic for wrapping in conference mode
    if (e.key === 'ArrowRight') nextIdx = Math.min(seats.length -1, idx + 1);
    else if (e.key === 'ArrowLeft') nextIdx = Math.max(0, idx - 1);
    else if (e.key === 'ArrowDown') nextIdx = Math.min(seats.length -1, idx + cols);
    else if (e.key === 'ArrowUp') nextIdx = Math.max(0, idx - cols);
    if (nextIdx !== idx) { seats[nextIdx].focus(); e.preventDefault(); }
  };

  if (renderMode === 'conference') {
    // Flatten seats into a grid; group by tableNumber as row sections.
    const rows = filteredTables.map(t => ({
      tableNumber: t.tableNumber,
      seats: t.seats
    }));
    return (
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
    <span style={{ fontSize:'0.6rem', fontWeight:600 }}>Open Seating</span>
          {highlightTable && (
            <button type="button" onClick={()=>setShowAll(s=>!s)} className="btn btn-small" aria-pressed={showAll} style={{ fontSize:'0.55rem' }}>
              {showAll ? 'Focus My Seat' : 'Show All'}
            </button>
          )}
        </div>
        <div ref={gridRef} role="grid" aria-label="Conference seating map" onKeyDown={handleKeyDown} tabIndex={0} style={{ outline:'none' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
          {rows.map(r => (
            <div key={r.tableNumber} style={{ border:'1px solid #ddd', borderRadius:6, padding:6, background:'#fff' }}>
              <div style={{ fontSize:'0.6rem', fontWeight:'600', marginBottom:4 }}>Section {r.tableNumber}</div>
              <div style={{ display:'grid', gap:4, gridTemplateColumns:'repeat(auto-fill,minmax(26px,1fr))' }}>
                {r.seats.map(seat => {
                  const isYou = Number(highlightTable) === r.tableNumber && (highlightSeats||[]).includes(seat.seatNumber);
                  const color = isYou ? '#198754' : seat.status === 'occupied' ? '#0d6efd' : seat.status === 'assigned' ? '#ffc107' : seat.status === 'reserved' ? '#fd7e14' : '#e9ecef';
                  const textColor = isYou ? '#fff' : '#333';
                  return (
                      <div
                        key={seat.seatNumber}
                        role="gridcell"
                        aria-label={`Section ${r.tableNumber} Seat ${seat.seatNumber}${isYou? ' - your seat':''}${seat.guestName? ` - ${seat.guestName}`:''}`}
                        data-seat-index
                        tabIndex={0}
                        title={seat.guestName || `Seat ${seat.seatNumber}`}
                        style={{
                          background:color, color:textColor, fontSize:'0.55rem', textAlign:'center', padding:'4px 0', borderRadius:4,
                          border:isYou ? '2px solid #145a32':'1px solid #ced4da'
                        }}
                      >
                        {seat.seatNumber}
                      </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        </div>
        <div ref={liveRegionRef} aria-live="polite" style={{ position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(1px,1px,1px,1px)' }} />
        {legend}
      </div>
    );
  }

  // Party mode: tables as cards with seat pills
  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:4 }}>
  <span style={{ fontSize:'0.6rem', fontWeight:600 }}>{seatingModeUnified === 'assigned' ? 'Assigned Table Seating' : 'Table Seating'}</span>
        {highlightTable && (
          <button type="button" onClick={()=>setShowAll(s=>!s)} className="btn btn-small" aria-pressed={showAll} style={{ fontSize:'0.55rem' }}>
            {showAll ? 'Focus My Table' : 'Show All'}
          </button>
        )}
      </div>
      <div ref={gridRef} role="grid" aria-label="Table seating map" onKeyDown={handleKeyDown} tabIndex={0} style={{ outline:'none', display:'grid', gap:8, gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))' }}>
        {filteredTables.map(t => {
          const isUserTable = Number(highlightTable) === t.tableNumber;
          return (
            <div key={t.tableId} role="row" style={{ border:'1px solid #dee2e6', borderRadius:8, padding:6, background:isUserTable?'#f0fdf4':'#fff', position:'relative' }}>
              <div style={{ fontSize:'0.65rem', fontWeight:600, marginBottom:4 }} aria-label={`Table ${t.tableNumber}${isUserTable? ' - your table':''}`}>Table {t.tableNumber}</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                {t.seats.map(seat => {
                  const isYou = isUserTable && (highlightSeats||[]).includes(seat.seatNumber);
                  const color = isYou ? '#198754' : seat.status === 'occupied' ? '#0d6efd' : seat.status === 'assigned' ? '#ffc107' : seat.status === 'reserved' ? '#fd7e14' : '#e9ecef';
                  const textColor = isYou ? '#fff' : '#333';
                  return (
                    <div
                      key={seat.seatNumber}
                      role="gridcell"
                      aria-label={`Table ${t.tableNumber} Seat ${seat.seatNumber}${isYou? ' - your seat':''}${seat.guestName? ` - ${seat.guestName}`:''}`}
                      data-seat-index
                      tabIndex={0}
                      title={seat.guestName || `Seat ${seat.seatNumber}`}
                      style={{
                        minWidth:26, padding:'3px 4px', borderRadius:12, fontSize:'0.55rem', background:color, color:textColor,
                        textAlign:'center', border:isYou ? '2px solid #145a32':'1px solid #ced4da'
                      }}>{seat.seatNumber}</div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div ref={liveRegionRef} aria-live="polite" style={{ position:'absolute', width:1, height:1, overflow:'hidden', clip:'rect(1px,1px,1px,1px)' }} />
      {legend}
    </div>
  );
}
